// src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import NotificationStatusButton from './NotificationStatusButton';
import { getOrCreateDeviceId, getAppVersion } from './utils/appUtils';
import useWebSocket from './hooks/useWebSocket';
import NotificationSettings from './components/NotificationSettings';
import MessageDisplay from './components/MessageDisplay';
import { getFcmToken as serviceGetFcmToken } from './services/firebaseService';
import { getMessages, saveMessages, getPhone, savePhone } from './utils/db';
import './App.css';

function App() {
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [deviceId, setDeviceId] = useState('');
    const [fcmToken, setFcmToken] = useState(null);
    const [expandedMsgs, setExpandedMsgs] = useState(new Set());
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [browserNotificationPermission, setBrowserNotificationPermission] = useState(Notification.permission);
    const [processMessage, setProcessMessage] = useState('');
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [showPhoneTooltip, setShowPhoneTooltip] = useState(false);
    const [statusMessages, setStatusMessages] = useState([]);

    const appVersion = getAppVersion();

    const {
        ws,
        connectionStatus,
        loggedIn,
        loggedInPhone,
        messages,
        isNotificationsEnabled,
        logincode,
        step,
        attemptsLeft,
        setStep,
        setLoggedInPhone,
        setMessages,
        setIsNotificationsEnabled,
        setLogincode,
        setAttemptsLeft,
        setProcessMessage: setProcessMessageFromHook,
        connectWebSocket,
        sendMessage,
        wsRef,
        messageTimeoutId
    } = useWebSocket(deviceId, fcmToken, setProcessMessage, setBrowserNotificationPermission);

    // Add status message with auto-removal
    const addStatusMessage = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now();
        const newMessage = { id, message, type, timestamp: new Date() };
        
        setStatusMessages(prev => [...prev, newMessage]);
        
        setTimeout(() => {
            setStatusMessages(prev => prev.filter(msg => msg.id !== id));
        }, duration);
    }, []);

    // Determine phone icon color
    const getPhoneIconColor = () => {
        if (!loggedInPhone) return 'red'; // No phone yet
        if (loggedIn && step === 'done') return 'green'; // Phone confirmed by server
        return 'orange'; // Phone only from local DB
    };

    // Determine notification icon color
    const getNotificationIconColor = () => {
        if (browserNotificationPermission !== 'granted') return 'red'; // Not granted by browser
        if (!isNotificationsEnabled) return 'orange'; // Granted by browser but not enabled on server
        return 'green'; // Enabled both on device and server
    };

    const getConnectionStatusClass = () => {
        if (connectionStatus.includes('Connected')) return 'connected';
        if (connectionStatus.includes('Connecting')) return 'connecting';
        return 'disconnected';
    };

    useEffect(() => {
        const updateStatus = () => {
            const online = navigator.onLine;
            setIsOffline(!online);
            const message = online ? 'You are back online.' : 'You are offline. Showing cached data.';
            addStatusMessage(message, online ? 'success' : 'warning');
        };

        window.addEventListener('online', updateStatus);
        window.addEventListener('offline', updateStatus);

        return () => {
            window.removeEventListener('online', updateStatus);
            window.removeEventListener('offline', updateStatus);
        };
    }, [addStatusMessage]);

    useEffect(() => {
        const preloadFromDB = async () => {
            const savedMsgs = await getMessages();
            if (savedMsgs?.length > 0) setMessages(savedMsgs);

            const savedPhone = await getPhone();
            if (savedPhone) setLoggedInPhone(savedPhone);

            if (!navigator.onLine && savedMsgs?.length > 0 && savedPhone) {
                setStep('done');
            }
        };

        preloadFromDB();
    }, []);

    // Save phone when login is complete
    useEffect(() => {
        if (loggedIn && step === 'done' && loggedInPhone) {
            savePhone(loggedInPhone);
        }
    }, [loggedIn, loggedInPhone, step]);

    // Save messages whenever they change (for authenticated users)
    useEffect(() => {
        console.log('Messages changed:', messages?.length, 'messages');
        console.log('LoggedIn:', loggedIn, 'LoggedInPhone:', loggedInPhone);
        
        if (messages && messages.length > 0 && (loggedIn || loggedInPhone)) {
            const uniqueMessages = [...new Map(messages.map(m => [m.id, m])).values()];
            console.log('Saving', uniqueMessages.length, 'unique messages to IndexedDB');
            
            saveMessages(uniqueMessages).then(() => {
                console.log('Messages successfully saved to IndexedDB');
            }).catch((error) => {
                console.error('Error saving messages to IndexedDB:', error);
            });
        } else {
            console.log('Not saving messages - conditions not met');
        }
    }, [messages, loggedIn, loggedInPhone]);

    useEffect(() => {
        const id = getOrCreateDeviceId();
        setDeviceId(id);
        setBrowserNotificationPermission(Notification.permission);

        const setupFCM = async () => {
            if (Notification.permission === 'granted') {
                //addStatusMessage('Attempting to get FCM token...', 'info');
                const token = await serviceGetFcmToken(setProcessMessage, setFcmToken, setBrowserNotificationPermission);
                setFcmToken(token);
            } else {
                addStatusMessage('Browser notification permission not granted. FCM may not be available.', 'warning');
            }
        };
        setupFCM();
    }, [addStatusMessage]);

    useEffect(() => {
        if ((browserNotificationPermission === 'denied' || browserNotificationPermission === 'unsupported') && isNotificationsEnabled) {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                sendMessage({ action: 'disable_notifications', deviceid: deviceId });
                addStatusMessage('Browser notifications denied or unsupported. Automatically disabling server notifications.', 'warning');
            }
            setIsNotificationsEnabled(false);
            setFcmToken(null);
        }
    }, [browserNotificationPermission, isNotificationsEnabled, deviceId, sendMessage, addStatusMessage, setIsNotificationsEnabled, setFcmToken, wsRef]);

    const sendPhone = () => {
        if (ws && phone.trim()) {
            sendMessage({ action: 'sendphone', deviceid: deviceId, phone: phone.trim() });
            addStatusMessage('📨 Phone sent, waiting for OTP...', 'info');
        }
    };

    const sendOTP = () => {
        if (ws && otp.trim()) {
            sendMessage({ action: 'login', deviceid: deviceId, logincode, otp: otp.trim() });
            addStatusMessage('🔐 Verifying OTP...', 'info');
        }
    };

    const toggleExpanded = (id) => {
        setExpandedMsgs(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    const handlePhoneClick = () => {
        setShowPhoneTooltip(!showPhoneTooltip);
        setTimeout(() => setShowPhoneTooltip(false), 3000);
    };

    const handleNotificationClick = async () => {
        const currentColor = getNotificationIconColor();

        if (currentColor === 'red') {
            // Request browser permission
            if (!('Notification' in window)) {
                addStatusMessage('Your browser does not support notifications.', 'error');
                setBrowserNotificationPermission('unsupported');
                return;
            }

            addStatusMessage('Requesting browser notification permission...', 'info');
            try {
                const permission = await Notification.requestPermission();
                setBrowserNotificationPermission(permission);

                if (permission === 'granted') {
                    addStatusMessage('✅ Browser permission granted. Getting FCM token...', 'success');
                    const token = await serviceGetFcmToken(setProcessMessage, setFcmToken, setBrowserNotificationPermission);
                    if (token) {
                        setFcmToken(token);
                        addStatusMessage('✅ FCM token obtained. Click notification icon again to enable server notifications.', 'success');
                    } else {
                        addStatusMessage('❌ Could not obtain FCM token after browser permission granted.', 'error');
                    }
                } else {
                    addStatusMessage('❌ Browser notification permission was denied.', 'error');
                }
            } catch (error) {
                console.error("Error requesting browser permission:", error);
                addStatusMessage('❌ Error requesting browser notification permission.', 'error');
            }
        } else if (currentColor === 'orange') {
            // Enable server notifications
            if (!loggedIn) {
                addStatusMessage("Please log in to manage notifications.", 'warning');
                return;
            }

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && fcmToken) {
                sendMessage({ action: 'enable_notifications', deviceid: deviceId, fcm: fcmToken });
                addStatusMessage('✅ Enabling notifications on server...', 'info');
                setIsNotificationsEnabled(true);
            } else {
                addStatusMessage('WebSocket not connected or FCM token not available. Try reconnecting.', 'error');
            }
        } else if (currentColor === 'green') {
            // Disable server notifications
            if (!loggedIn) {
                addStatusMessage("Please log in to manage notifications.", 'warning');
                return;
            }

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                sendMessage({ action: 'disable_notifications', deviceid: deviceId });
                addStatusMessage('🔕 Disabling notifications on server...', 'info');
                setIsNotificationsEnabled(false);
            } else {
                addStatusMessage('WebSocket not connected. Try reconnecting.', 'error');
            }
        }
    };

    return (
        <>
            {/* Header */}
            <header className="app-header">
                <div className="app-header__inner">
                    <div className="app-header__logo">
                        <a href="/" target="_top">
                            <img 
                                src="/logos/WS-logo-horizontal-white-400x120.png" 
                                alt="WappSync"
                                width="167" 
                                height="50"
                            />
                        </a>
                    </div>

                    <div className="app-header__icons">
                        {/* Phone Icon */}
                        <div
                            className="icon-button-wrapper"
                            onClick={handlePhoneClick}
                            title={loggedInPhone || 'No phone number'}
                        >
                            <span className="icon-button">
                                📱
                            </span>
                            <span className={`status-dot ${getPhoneIconColor()}`}></span>
                            {showPhoneTooltip && loggedInPhone && (
                                <div className="icon-tooltip" style={{ opacity: 1, visibility: 'visible' }}>
                                    {loggedInPhone}
                                </div>
                            )}
                        </div>

                        {/* Notification Icon */}
                        <div
                            className="icon-button-wrapper"
                            onClick={handleNotificationClick}
                            title={
                                getNotificationIconColor() === 'red' ? 'Click to enable browser notifications' :
                                getNotificationIconColor() === 'orange' ? 'Click to enable server notifications' :
                                'Click to disable notifications'
                            }
                        >
                            <span className="icon-button">
                                {getNotificationIconColor() === 'green' ? '🔔' : '🔕'}
                            </span>
                            <span className={`status-dot ${getNotificationIconColor()}`}></span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Status Bar */}
            <div className="status-bar">
                <div className="status-bar__inner">
                    <div className="status-bar__messages">
                        <span className={`status-bar__connection-status status-bar__connection-status--${getConnectionStatusClass()}`}>
                            {connectionStatus}
                        </span>
                        
                        {statusMessages.slice(-2).map(msg => (
                            <span
                                key={msg.id}
                                className={`status-message--${msg.type}`}
                            >
                                {msg.message}
                            </span>
                        ))}
                    </div>
                    
                    {/* <span className="status-bar__version">
                        v{appVersion}
                    </span> */}
                </div>
            </div>

            {/* Main Content */}
            <main className="main-content">
                <div className="wappsync-container">
                    <div className="wappsync-card">

                        {/* Phone Input Step */}
                        {step === 'phone' && (
                            <div className="form-section">
                                <input 
                                    type="text" 
                                    placeholder="Enter phone number" 
                                    value={phone} 
                                    onChange={(e) => setPhone(e.target.value)} 
                                    className="wappsync-input"
                                />
                                <br />
                                <button 
                                    onClick={sendPhone} 
                                    disabled={!phone.trim()} 
                                    className="wappsync-button wappsync-button--primary"
                                >
                                    Send Phone
                                </button>
                            </div>
                        )}

                        {/* OTP Input Step */}
                        {step === 'otp' && (
                            <div className="form-section">
                                <p className="wappsync-logincode">
                                    Logincode: <span>{logincode}</span>
                                </p>
                                <input 
                                    type="text" 
                                    placeholder="Enter OTP" 
                                    value={otp} 
                                    onChange={(e) => setOtp(e.target.value)} 
                                    className="wappsync-input"
                                />
                                <br />
                                <button 
                                    onClick={sendOTP} 
                                    disabled={!otp.trim()} 
                                    className="wappsync-button wappsync-button--primary"
                                >
                                    Send OTP
                                </button>
                                {attemptsLeft !== null && (
                                    <p className={`wappsync-otp-attempts ${attemptsLeft <= 2 ? 'low' : ''}`}>
                                        Attempts left: {attemptsLeft}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Message Display */}
                        {((loggedIn && step === 'done') || (!loggedIn && messages && messages.length > 0)) && (
                            <>
                                {!loggedIn && (
                                    <div className="offline-banner">
                                        📱 Viewing saved messages offline. Please log in to get live updates.
                                    </div>
                                )}
                                <MessageDisplay
                                    messages={messages}
                                    expandedMsgs={expandedMsgs}
                                    toggleExpanded={toggleExpanded}
                                />
                            </>
                        )}

                        {/* Notification Settings Modal */}
                        {showNotificationsModal && loggedIn && (
                            <NotificationSettings
                                isEnabled={isNotificationsEnabled}
                                onClose={() => setShowNotificationsModal(false)}
                                deviceId={deviceId}
                                sendMessage={sendMessage}
                                setIsNotificationsEnabled={setIsNotificationsEnabled}
                                fcmToken={fcmToken}
                                setProcessMessage={setProcessMessage}
                            />
                        )}
                    </div>
                </div>
            </main>
        </>
    );
}

export default App;